import React from 'react'
import { useResolvedNav } from 'config/navigation/useResolvedNav'
import { Setting2 } from 'iconsax-react'
import { useHistory, useLocation } from 'react-router-dom'
import styled, { css } from 'styled-components'

import useWindowWidth from 'utils/hooks/useWindowWidth'

import { ChevronRight } from 'components/lib/DesignSystem/Icons'
import Tooltip from 'components/lib/DesignSystem/V3/Tooltip'

import { FLYOUT_SIDE_OFFSET, navRowHover, navSectionActive } from '../navStyles'

// The Settings section is the pinnedBottom node in the resolved tree.
const findSettingsNode = (tree) =>
  Array.isArray(tree) ? tree.find((n) => n.pinnedBottom) : undefined

// Collapsed-hover flyout surfaces the "Settings highlight pages" (⭐ highlight:true)
// per the Navigation Menu spec, alongside "View all settings".
const collectHighlights = (settingsNode) => {
  if (!settingsNode || !Array.isArray(settingsNode.children)) return []
  return settingsNode.children.filter((c) => c.visible && c.highlight)
}

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const Root = styled.div`
  position: relative;
  flex-shrink: 0;
`

// The pinned Settings trigger row — mirrors the L1 section row (Figma 1:26644):
// 24px icon + Poppins SemiBold 14px label + right chevron; height 36px, radius 10px.
const TriggerRow = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 36px;
  padding: 0 12px;
  border-radius: 10px;
  cursor: pointer;
  box-sizing: border-box;
  font-family: Poppins, sans-serif;
  font-size: 14px;
  /* Figma 1:26760: the pinned Settings label is Medium (500), not SemiBold. */
  font-weight: 500;
  line-height: 20px;
  color: var(--sidebar-item-text);
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  svg,
  svg path {
    fill: currentColor;
  }

  ${navRowHover}

  ${({ $open }) => $open && navSectionActive}

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  /* Collapsed icon button — Figma node 1:17494 (Settings): 42×40, matching the
     L1 section rows. */
  ${({ $collapsed }) =>
    $collapsed &&
    css`
      justify-content: center;
      width: 42px;
      height: 40px;
      padding: 0;
      border-radius: 12px;
      margin: 0 auto;
    `}
`

// Icon recolors to var(--primary) when the settings sub-view is open, matching
// NavSection.js's IconWrapper ($activeIcon) — the same "active section" affordance.
const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  color: ${({ $active }) => ($active ? 'var(--primary)' : 'currentColor')};
`

const Label = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

// Right-pointing, and deliberately NOT rotating: this row does not expand an
// inline accordion, it replaces the whole rail body with SettingsPanelView. A
// down chevron promised a dropdown that never appears. Matches ProfileMenu's
// "Help & Support" row, which uses a right arrow for the same reason.
const ChevronWrap = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  margin-left: auto;

  svg,
  svg path {
    fill: currentColor;
  }
`

// Collapsed-hover flyout content: "View all settings" + the highlight pages.
// Geometry mirrors NavFlyout's FlyoutPanel so both collapsed flyouts share the
// same left inset (the -4px margin trims the shared Tooltip popup's 10px padding
// to 6px, then the title/rows add their own left padding on top).
const FlyoutRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 188px;
  margin: -4px;
  font-family: Poppins, sans-serif;
`

// Flyout title — bold "Settings" heading at the top of the collapsed-hover
// flyout (Figma "Settings HOVER"). 12px left inset matches NavFlyout's header.
const FlyoutTitle = styled.div`
  padding: 8px 12px 2px;
  font-size: 14px;
  font-weight: 700;
  color: var(--sidebar-item-text);
`

// Rows sit 18px in from the panel's left edge — same as NavFlyout's children so
// the two collapsed flyouts read identically.
const FlyoutHeader = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  padding: 8px 12px 8px 18px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  color: var(--sidebar-item-text);

  &:hover {
    background-color: var(--sidebar-item-hover-background);
    color: var(--sidebar-item-text);
  }
`

const FlyoutItem = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  padding: 8px 12px 8px 18px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  color: var(--sidebar-item-text);

  &:hover {
    background-color: var(--sidebar-item-hover-background);
    color: var(--sidebar-item-text);
  }
`

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * SettingsNav — pinned-bottom Settings trigger for the side nav.
 *
 * Clicking the trigger (or "View all settings" in the collapsed-hover flyout)
 * opens the full settings sub-view, which SideNav renders as a full-rail panel
 * (the rail expands to full width while it's open — "extended settings mode").
 * Open/close state is owned by the layout; this component only signals intent.
 *
 * Props (contract with SideNav slot):
 *   collapsed  {boolean}  — whether the side-nav rail is currently collapsed.
 *                           When true only the icon is shown, and hovering opens
 *                           a flyout with "View all settings" + highlight pages.
 *   open       {boolean}  — whether the settings sub-view is currently open
 *                           (used for the trigger's selected styling).
 *   onOpen     {() => void} — called when the user asks to open the settings
 *                           sub-view.
 */
const SettingsNav = ({ collapsed, open, onOpen }) => {
  const history = useHistory()
  const { pathname } = useLocation()
  const tree = useResolvedNav(undefined, pathname)
  const highlights = collectHighlights(findSettingsNode(tree))
  const windowWidth = useWindowWidth()
  const mobileBlocked = windowWidth > 0 && windowWidth <= 1050

  // Navigate a highlight entry: a page uses its own path; a folder routes to its
  // first visible child (folders never navigate themselves).
  const highlightPath = (node) => {
    if (node.resolvedPath) return node.resolvedPath
    const firstChild = Array.isArray(node.children)
      ? node.children.find((c) => c.visible && c.resolvedPath)
      : undefined
    return firstChild ? firstChild.resolvedPath : undefined
  }

  const handleHighlightClick = (node) => {
    const path = highlightPath(node)
    if (path) history.push(path)
  }

  const flyoutContent = (
    <FlyoutRoot>
      <FlyoutTitle>Settings</FlyoutTitle>
      <FlyoutHeader id="settings-flyout-header" type="button" onClick={onOpen}>
        View all settings
      </FlyoutHeader>
      {highlights.map((node) => (
        <FlyoutItem
          key={node.id}
          id={`settings-flyout-item-${node.id}`}
          type="button"
          onClick={() => handleHighlightClick(node)}
        >
          {node.label}
        </FlyoutItem>
      ))}
    </FlyoutRoot>
  )

  const trigger = (
    <TriggerRow
      id="settings-flyout-trigger"
      type="button"
      $collapsed={collapsed}
      $open={open}
      aria-label={collapsed ? 'Settings' : undefined}
      aria-expanded={open}
      aria-disabled={mobileBlocked}
      disabled={mobileBlocked}
      onClick={mobileBlocked ? undefined : onOpen}
    >
      <IconWrap $active={open}>
        {/* Figma 1:26758: vuesax/outline/setting-2 — 20px expanded, 24px in the
            collapsed rail to match the other collapsed section icons. Bold when
            the settings sub-view is open, mirroring every other L1 section's
            Outline->Bold swap on active (NavSection.js) — the icon-consistency
            fix for the "no consistency in selection icon behavior" feedback. */}
        <Setting2
          variant={open ? 'Bold' : 'Outline'}
          size={collapsed ? 24 : 20}
          color="currentColor"
        />
      </IconWrap>
      {!collapsed && (
        <>
          <Label>Settings</Label>
          <ChevronWrap aria-hidden="true">
            <ChevronRight noDefaultFill fill="currentColor" width="16px" height="16px" />
          </ChevronWrap>
        </>
      )}
    </TriggerRow>
  )

  return (
    <Root>
      {collapsed ? (
        <Tooltip
          content={flyoutContent}
          side="right"
          align="start"
          delay={150}
          closeDelay={100}
          arrow
          connector="diamond"
          surface="elevated"
          /* Shared with NavFlyout / LockedNavItem so the diamond sits the same
             distance from the rail in every collapsed flyout. */
          sideOffset={FLYOUT_SIDE_OFFSET}
        >
          {trigger}
        </Tooltip>
      ) : (
        trigger
      )}
    </Root>
  )
}

export default SettingsNav
